// MapOverlay.jsx - Custom overlay components for the map
import React, { useState, useEffect, useCallback } from 'react';
import { Typography, IconButton, Badge } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import moment from 'moment';
import 'moment/locale/th';
import 'moment/locale/zh-cn';
import { createRoot } from 'react-dom/client';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import SavedItemsService from '../../services/savedItemsService';

import { getMarkerIcon, getMarkerBadgeIcon, dateLabels, setMomentLocale } from './MapUtils';

// Mobile detection utility
const isMobile = () => {
  return window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const getMarkerDimensions = () => (
  isMobile()
    ? { width: 52, height: 52 }
    : { width: 116, height: 106 }
);

const getPlaceName = (place) => place?.event_name || place?.place_name || place?.name || 'Interesting spot';

const getPlaceImage = (place) => (
  place?.generated_thumbnail_url ||
  place?.event_image_link ||
  place?.image_url ||
  place?.image ||
  place?.photo_url ||
  place?.thumbnail_url ||
  ''
);

const getStatusText = (place, language) => {
  const type = `${place?.type || place?.main_category || place?.category || ''}`.toLowerCase();
  if (type.includes('promotion')) return language === 'TH' ? 'ดีลแรง' : language === 'CN' ? '热门优惠' : 'Hot deal';
  if (type.includes('review') || place?._source === 'review') return language === 'TH' ? 'ยอดนิยม' : language === 'CN' ? '人气' : 'Popular';
  if (type.includes('concert') || type.includes('music') || type.includes('performance')) return language === 'TH' ? 'ใกล้เริ่ม' : language === 'CN' ? '即将开始' : 'Starts soon';
  if (type.includes('food') || type.includes('restaurant')) return language === 'TH' ? 'ฮอต' : language === 'CN' ? '火热' : 'Hot';
  if (type.includes('night')) return language === 'TH' ? 'กำลังฮิต' : language === 'CN' ? '趋势' : 'Trending';
  return language === 'TH' ? 'คึกคัก' : language === 'CN' ? '热闹' : 'Busy';
};

const getMarkerAccent = (place) => {
  const type = `${place?.type || place?.main_category || place?.category || ''}`.toLowerCase();
  if (place?._source === 'review' || type.includes('review')) return '#22c55e';
  if (type.includes('concert') || type.includes('music') || type.includes('performance')) return '#8b5cf6';
  if (type.includes('food') || type.includes('restaurant')) return '#ef4444';
  if (type.includes('promotion')) return '#f97316';
  if (type.includes('travel') || type.includes('place') || type.includes('venue')) return '#3b82f6';
  return '#ff7a1a';
};

// Overlay content component for each marker
export const OverlayContent = ({ groupData, visible, setVisible, onMarkerClick, language, selectedEventId, disablePopups = false, onInfoBoxClick, onCreatorClick }) => {
  const { places } = groupData;
  const [isMobileView, setIsMobileView] = useState(isMobile());

  // Derive currentIndex from selectedEventId (read-only, no local state)
  const getSelectedIndex = useCallback(() => {
    if (selectedEventId !== null && selectedEventId !== undefined && places) {
      const foundIndex = places.findIndex(place => String(place.id) === String(selectedEventId));
      return foundIndex >= 0 ? foundIndex : 0;
    }
    return 0;
  }, [selectedEventId, places]);

  const [localIndex, setLocalIndex] = useState(getSelectedIndex);
  const currentIndex = places.length ? Math.min(localIndex, places.length - 1) : 0;
  const currentPlace = places[currentIndex];

  // Saved bookmark states
  const [isSaved, setIsSaved] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const token = localStorage.getItem('token');
  const isAuthenticated = !!token;

  const rawId = currentPlace?.event_id || currentPlace?.id;
  const isReview = currentPlace?._source === 'review';
  const isVenue = currentPlace?._source === 'venue';
  const savedItemType = isReview ? 'review' : isVenue ? 'venue' : 'event';
  const savedItemId = (() => {
    if (typeof rawId === 'string' && rawId.startsWith('review_')) return rawId.replace('review_', '');
    return String(parseInt(rawId, 10) || rawId);
  })();

  // Fetch save status
  useEffect(() => {
    if (!isAuthenticated || !savedItemId) {
      setIsSaved(false);
      return;
    }
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    SavedItemsService.getSaveStatus(savedItemId, savedItemType, authHeaders)
      .then(({ saved }) => setIsSaved(saved))
      .catch((err) => console.error('[MapOverlay] getSaveStatus error:', err));
  }, [isAuthenticated, savedItemId, savedItemType, token]);

  const handleToggleSave = async (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      alert(language === 'TH' ? 'กรุณาเข้าสู่ระบบเพื่อบันทึกรายการ' : language === 'CN' ? '请登录以保存项目' : 'Please log in to save items');
      return;
    }
    if (saveLoading) return;
    setSaveLoading(true);
    try {
      const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
      const displayData = {
        item_name: currentPlace.event_name || currentPlace.name || currentPlace.place_name || '',
        item_image_url: currentPlace.generated_image_url || currentPlace.image || currentPlace.image_url || currentPlace.event_image_link || currentPlace.main_image_url || '',
        item_location: currentPlace.event_location || currentPlace.location || currentPlace.address || '',
        item_date: currentPlace.event_date || currentPlace.created_at || '',
      };
      const { saved } = await SavedItemsService.toggleSave(savedItemId, savedItemType, displayData, authHeaders);
      setIsSaved(saved);
    } catch (err) {
      console.error('[MapOverlay] save error:', err);
    } finally {
      setSaveLoading(false);
    }
  };

  // Helper for creator name display
  const getCreatorName = (place, lang) => {
    const name = place?._source === 'review'
      ? (place?.reviewer_name || place?.user_name || place?.creator_name || place?.creator || '')
      : (place?.creator_name || place?.reviewer_name || place?.organizer || place?.user_name || place?.creator || '');
    if (name.trim()) return name;
    if (place?.main_category === 'Venue') {
      return lang === 'TH' ? 'ผู้ประกอบการ' : lang === 'CN' ? '商家' : 'Merchant';
    }
    return lang === 'TH' ? 'ผู้โพสต์' : lang === 'CN' ? '发布者' : 'Poster';
  };

  const handleCreatorClick = (e) => {
    e.stopPropagation();
    if (onCreatorClick) {
      onCreatorClick({
        name: getCreatorName(currentPlace, language),
        id: currentPlace?.event_creator_id || currentPlace?.venue_creator_id || currentPlace?.user_id || currentPlace?.reviewer_id || null
      });
    }
  };

  // Debug logging for overlay selection sync
  useEffect(() => {
    const nextIndex = getSelectedIndex();
    setLocalIndex(nextIndex);

    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 OverlayContent: selectedEventId changed:', {
        selectedEventId,
        currentIndex: nextIndex,
        currentPlaceId: places[nextIndex]?.id,
        groupPlacesCount: places.length
      });
    }
  }, [selectedEventId, places, getSelectedIndex]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(isMobile());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const iconWidth = 32;
  const iconHeight = 32;
  const markerSize = isMobileView ? 36 : 58;
  const markerPadding = isMobileView ? 2 : 3;
  const cornerIconSize = isMobileView ? 20 : 31;
  const cornerIconGlyphSize = isMobileView ? 12 : 18;
  const labelTop = isMobileView ? 47 : 62;
  const labelMinWidth = isMobileView ? 58 : 96;
  const labelMaxWidth = isMobileView ? 82 : 124;
  const markerIcon = getMarkerIcon(currentPlace);
  const MarkerBadgeIcon = getMarkerBadgeIcon(currentPlace);
  const markerImage = getPlaceImage(currentPlace);
  const markerAccent = getMarkerAccent(currentPlace);
  const statusText = getStatusText(currentPlace, language);
  const placeName = getPlaceName(currentPlace);
  
  const shortName = placeName.length > 15 ? placeName.substring(0, 12) + '...' : placeName;
  
  // Get appropriate labels for the current language
  const labels = dateLabels[language] || dateLabels.EN;
  
  // Set moment locale based on language
  const locale = setMomentLocale(language);
  moment.locale(locale);

  const handleMarkerClick = (e) => {
    e.stopPropagation();
    if (onMarkerClick) onMarkerClick(groupData.id);
    setVisible(!visible);
  };


  const handleCloseClick = (e) => {
    e.stopPropagation();
    setVisible(false);
  };

  const handleNextEvent = (e) => {
    e.stopPropagation();
    if (places.length > 1) {
      setLocalIndex((idx) => (idx + 1) % places.length);
    }
  };

  const handlePrevEvent = (e) => {
    e.stopPropagation();
    if (places.length > 1) {
      setLocalIndex((idx) => (idx - 1 + places.length) % places.length);
    }
  };

  const handleDetailClick = (e) => {
    e.stopPropagation();
    window.location.href = `/event/${currentPlace.id}`;
  };

  // Format date in the selected language
  const formatDate = (date) => {
    if (!date) return '';
    return moment(date).format('DD-MMM-YY');
  };


  return (
    <div style={{ position: 'relative', display: 'inline-block', width: `${getMarkerDimensions().width}px`, height: `${getMarkerDimensions().height}px`, pointerEvents: 'none' }}>
      {/* The marker icon with badge for multiple events */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          cursor: 'pointer',
          zIndex: 2,
          pointerEvents: 'auto',
        }}
        onClick={handleMarkerClick}
        title={places.length > 1 ? `${places.length} events at this location` : placeName}
      >
        {/* Name tag just above the marker - disappears when info box is open */}
        {(!visible || disablePopups) && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              handleMarkerClick(e);
            }}
            style={{
              position: 'absolute',
              bottom: 'calc(100% + 4px)',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(17, 24, 39, 0.85)',
              backdropFilter: 'blur(4px)',
              color: '#ffffff',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: isMobileView ? '9px' : '11px',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              border: `1px solid ${markerAccent}bb`,
              boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
              zIndex: 10,
              cursor: 'pointer',
              pointerEvents: 'auto'
            }}
          >
            {shortName}
          </div>
        )}

        <Badge
          badgeContent={places.length > 1 ? places.length : null}
          color="primary"
          overlap="circular"
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{
            '& .MuiBadge-badge': {
              minWidth: isMobileView ? 16 : 20,
              height: isMobileView ? 16 : 20,
              fontSize: isMobileView ? 10 : 12,
              padding: isMobileView ? '0 4px' : '0 6px',
            }
          }}
        >
          <div style={{
            width: `${markerSize}px`,
            height: `${markerSize}px`,
            borderRadius: '50%',
            padding: `${markerPadding}px`,
            background: markerAccent,
            boxShadow: isMobileView
              ? `0 6px 14px ${markerAccent}45, 0 1px 5px rgba(17,24,39,.16)`
              : `0 10px 24px ${markerAccent}55, 0 2px 8px rgba(17,24,39,.18)`,
            position: 'relative',
          }}>
            {markerImage ? (
              <img
                src={markerImage}
                alt={placeName}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '50%',
                  display: 'block',
                  backgroundColor: '#fff'
                }}
              />
            ) : (
              <div style={{
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                background: 'linear-gradient(135deg, rgba(255,255,255,.95), rgba(255,255,255,.7))'
              }}>
                <img
                  src={markerIcon}
                  alt={placeName}
                  style={{ width: iconWidth, height: iconHeight, objectFit: 'contain' }}
                />
              </div>
            )}
            <div style={{
              position: 'absolute',
              right: isMobileView ? '-4px' : '-7px',
              bottom: isMobileView ? '-5px' : '-10px',
              width: `${cornerIconSize}px`,
              height: `${cornerIconSize}px`,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              background: markerAccent,
              border: '2px solid #fff',
              boxShadow: isMobileView ? '0 3px 8px rgba(17,24,39,.16)' : '0 6px 12px rgba(17,24,39,.18)'
            }}>
              <MarkerBadgeIcon sx={{ width: cornerIconGlyphSize, height: cornerIconGlyphSize, color: '#fff' }} aria-hidden="true" />
            </div>
          </div>
        </Badge>
      </div>

      {/* The popup/overlay */}
      {visible && !disablePopups && (
        <div
          onClick={(e) => {
            if (onInfoBoxClick) {
              onInfoBoxClick(currentPlace);
            }
          }}
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 8px)',
            left: '50%',
            transform: 'translateX(-50%)',
            width: isMobileView ? '220px' : '280px',
            background: 'rgba(17, 24, 39, 0.95)',
            backdropFilter: 'blur(8px)',
            color: '#ffffff',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: '12px',
            boxShadow: '0 12px 32px rgba(0,0,0,0.4)',
            fontSize: isMobileView ? '11px' : '12px',
            zIndex: 1000,
            cursor: 'pointer',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            pointerEvents: 'auto',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Banner Image or Fallback Gradient */}
          {markerImage ? (
            <div style={{ position: 'relative', width: '100%', height: isMobileView ? '85px' : '120px' }}>
              <img
                src={markerImage}
                alt={placeName}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to bottom, rgba(17,24,39,0.3) 0%, rgba(17,24,39,0) 50%, rgba(17,24,39,0.7) 100%)'
              }} />
              {/* Creator/Poster Tag */}
              <div
                onClick={handleCreatorClick}
                style={{
                  position: 'absolute',
                  bottom: '8px',
                  left: '8px',
                  backgroundColor: 'rgba(17, 24, 39, 0.8)',
                  backdropFilter: 'blur(4px)',
                  color: '#ffffff',
                  padding: '3px 10px',
                  borderRadius: '20px',
                  fontSize: isMobileView ? '9px' : '11px',
                  fontWeight: 650,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderLeft: `3px solid ${markerAccent}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  cursor: 'pointer',
                  pointerEvents: 'auto',
                  transition: 'all 0.2s ease',
                  maxWidth: '75%',
                  zIndex: 1003
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(17, 24, 39, 0.95)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(17, 24, 39, 0.8)';
                  e.currentTarget.style.transform = 'translateY(0px)';
                }}
              >
                <span style={{ fontSize: isMobileView ? '10px' : '12px', opacity: 0.85 }}>👤</span>
                <span style={{ 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap'
                }}>
                  {getCreatorName(currentPlace, language)}
                </span>
              </div>
              {/* Save Button */}
              <IconButton
                onClick={handleToggleSave}
                disabled={saveLoading}
                size="small"
                sx={{
                  position: 'absolute',
                  bottom: '8px',
                  right: '8px',
                  color: isSaved ? '#a78bfa' : 'rgba(255, 255, 255, 0.75)',
                  backgroundColor: 'rgba(17, 24, 39, 0.6)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  zIndex: 1003,
                  padding: '4px',
                  backdropFilter: 'blur(4px)',
                  '&:hover': {
                    backgroundColor: 'rgba(17, 24, 39, 0.8)',
                    color: isSaved ? '#c084fc' : '#ffffff',
                  }
                }}
              >
                {isSaved ? <BookmarkIcon sx={{ fontSize: isMobileView ? '14px' : '18px' }} /> : <BookmarkBorderIcon sx={{ fontSize: isMobileView ? '14px' : '18px' }} />}
              </IconButton>
            </div>
          ) : (
            <div style={{
              position: 'relative',
              width: '100%',
              height: isMobileView ? '50px' : '75px',
              background: `linear-gradient(135deg, ${markerAccent}aa 0%, #111827 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img
                src={markerIcon}
                alt={statusText}
                style={{
                  width: isMobileView ? '24px' : '32px',
                  height: isMobileView ? '24px' : '32px',
                  opacity: 0.8,
                  objectFit: 'contain'
                }}
              />
              {/* Creator/Poster Tag */}
              <div
                onClick={handleCreatorClick}
                style={{
                  position: 'absolute',
                  bottom: '8px',
                  left: '8px',
                  backgroundColor: 'rgba(17, 24, 39, 0.8)',
                  backdropFilter: 'blur(4px)',
                  color: '#ffffff',
                  padding: '3px 10px',
                  borderRadius: '20px',
                  fontSize: isMobileView ? '9px' : '11px',
                  fontWeight: 650,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderLeft: `3px solid ${markerAccent}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  cursor: 'pointer',
                  pointerEvents: 'auto',
                  transition: 'all 0.2s ease',
                  maxWidth: '75%',
                  zIndex: 1003
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(17, 24, 39, 0.95)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(17, 24, 39, 0.8)';
                  e.currentTarget.style.transform = 'translateY(0px)';
                }}
              >
                <span style={{ fontSize: isMobileView ? '10px' : '12px', opacity: 0.85 }}>👤</span>
                <span style={{ 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap'
                }}>
                  {getCreatorName(currentPlace, language)}
                </span>
              </div>
              {/* Save Button */}
              <IconButton
                onClick={handleToggleSave}
                disabled={saveLoading}
                size="small"
                sx={{
                  position: 'absolute',
                  bottom: '8px',
                  right: '8px',
                  color: isSaved ? '#a78bfa' : 'rgba(255, 255, 255, 0.75)',
                  backgroundColor: 'rgba(17, 24, 39, 0.6)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  zIndex: 1003,
                  padding: '4px',
                  backdropFilter: 'blur(4px)',
                  '&:hover': {
                    backgroundColor: 'rgba(17, 24, 39, 0.8)',
                    color: isSaved ? '#c084fc' : '#ffffff',
                  }
                }}
              >
                {isSaved ? <BookmarkIcon sx={{ fontSize: isMobileView ? '14px' : '18px' }} /> : <BookmarkBorderIcon sx={{ fontSize: isMobileView ? '14px' : '18px' }} />}
              </IconButton>
            </div>
          )}

          {/* Close Button x */}
          <IconButton
            aria-label="close"
            onClick={handleCloseClick}
            size="small"
            sx={{
              position: 'absolute',
              top: '6px',
              right: '6px',
              padding: '4px',
              color: '#ffffff',
              backgroundColor: 'rgba(17, 24, 39, 0.6)',
              backdropFilter: 'blur(4px)',
              zIndex: 1002,
              '&:hover': {
                backgroundColor: 'rgba(17, 24, 39, 0.8)',
              }
            }}
          >
            <CloseIcon sx={{ fontSize: isMobileView ? '14px' : '18px' }} />
          </IconButton>

          {/* Info Content */}
          <div style={{ padding: isMobileView ? '8px 10px' : '12px 14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            
            {/* Navigation for multiple items grouped at same spot */}
            {places.length > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                padding: '2px 8px',
                borderRadius: '6px',
                marginBottom: '2px'
              }}>
                <IconButton
                  size="small"
                  onClick={handlePrevEvent}
                  sx={{ color: '#fff', padding: '2px' }}
                >
                  <ArrowBackIcon fontSize="inherit" />
                </IconButton>
                <span style={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.7)', fontWeight: 700 }}>
                  {currentIndex + 1} / {places.length}
                </span>
                <IconButton
                  size="small"
                  onClick={handleNextEvent}
                  sx={{ color: '#fff', padding: '2px' }}
                >
                  <ArrowForwardIcon fontSize="inherit" />
                </IconButton>
              </div>
            )}

            {/* Title */}
            <Typography variant="subtitle1" sx={{
              fontSize: isMobileView ? '12px' : '14px',
              fontWeight: 800,
              lineHeight: 1.3,
              color: '#ffffff',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {placeName}
            </Typography>

            {/* Dates */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: isMobileView ? '10px' : '11px',
              marginTop: '2px'
            }}>
              <span style={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.5)' }}>
                {isReview ? (labels.reviewDate || 'Review Date') : (labels.start || 'Start')}:
              </span>
              <span>{formatDate(currentPlace.event_date)}</span>
              {currentPlace.event_end_date && (
                <>
                  <span style={{ color: 'rgba(255, 255, 255, 0.3)' }}>|</span>
                  <span style={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.5)' }}>{labels.end || 'End'}:</span>
                  <span>{formatDate(currentPlace.event_end_date)}</span>
                </>
              )}
            </div>

            {/* Location */}
            {currentPlace.event_location && (
              <div style={{
                color: 'rgba(255, 255, 255, 0.65)',
                fontSize: isMobileView ? '9px' : '10px',
                lineHeight: 1.3,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                marginTop: '1px'
              }}>
                📍 {currentPlace.event_location}
              </div>
            )}

            {/* Description/Summary */}
            {(currentPlace.event_summary || currentPlace.summary || currentPlace.description) && (
              <div style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: isMobileView ? '10px' : '11px',
                lineHeight: 1.4,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                marginTop: '4px',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                paddingTop: '6px'
              }}>
                {currentPlace.event_summary || currentPlace.summary || currentPlace.description}
              </div>
            )}

            {/* Footer with views and details/website button */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '4px',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              paddingTop: '6px'
            }}>
              {/* Views */}
              {currentPlace.view_count > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '3px', color: 'rgba(255, 255, 255, 0.5)', fontSize: '10px' }}>
                  <span style={{ fontSize: '11px' }}>👁️</span>
                  <span>{currentPlace.view_count}</span>
                </div>
              ) : <div />}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                {(currentPlace.website_link || currentPlace.website) && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(currentPlace.website_link || currentPlace.website, '_blank');
                    }}
                    style={{
                      color: '#60a5fa',
                      fontWeight: 600,
                      fontSize: '10px',
                      cursor: 'pointer',
                      textDecoration: 'underline'
                    }}
                  >
                    {language === 'TH' ? 'เว็บไซต์' : language === 'CN' ? '网站' : 'Website'}
                  </span>
                )}

              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

// Custom Overlay class factory - created dynamically after Google Maps API is loaded
export const createCustomOverlayClass = (mapApi) => {
  return class CustomOverlay extends mapApi.OverlayView {
    constructor(position, groupData, map, initialVisible = false, onMarkerClick, language, selectedEventId = null, disablePopups = false, onInfoBoxClick, onCreatorClick) {
      super();
      this.position = position;
      this.groupData = groupData;
      this.map = map;
      this.visible = initialVisible;
      this.onMarkerClick = onMarkerClick;
      this.language = language;
      this.selectedEventId = selectedEventId;
      this.disablePopups = disablePopups;
      this.onInfoBoxClick = onInfoBoxClick;
      this.onCreatorClick = onCreatorClick;
      this.div = null;
      this.root = null;
      const markerDimensions = getMarkerDimensions();
      this.markerWidth = markerDimensions.width;
      this.markerHeight = markerDimensions.height;
      this.setVisible = this.setVisible.bind(this);
      this.renderOverlay = this.renderOverlay.bind(this);
      this.setMap(map);
    }

    onAdd() {
      if (!this.div) {
        this.div = document.createElement('div');
        this.div.style.position = 'absolute';
        this.div.style.willChange = 'transform';
        this.div.style.transform = 'translate3d(0,0,0)';
        this.div.style.zIndex = this.visible ? '1001' : '1';
      }

      if (!this.root) {
        this.root = createRoot(this.div);
      }
      this.renderOverlay();

      const panes = this.getPanes();
      if (panes?.overlayMouseTarget) {
        panes.overlayMouseTarget.appendChild(this.div);
      }
    }

    draw() {
      const projection = this.getProjection();
      if (!projection || !this.div) return;

      const latLng = new mapApi.LatLng(this.position.lat, this.position.lng);
      const pixel = projection.fromLatLngToDivPixel(latLng);

      if (pixel) {
        const markerDimensions = getMarkerDimensions();
        this.markerWidth = markerDimensions.width;
        this.markerHeight = markerDimensions.height;
        // Center-bottom of the marker image aligns exactly with the lat/lng coordinate:
        // - horizontally: shift left by half the icon width
        // - vertically: shift up by the full icon height so the bottom edge is at pixel.y
        const offsetX = pixel.x - this.markerWidth / 2;
        const offsetY = pixel.y - this.markerHeight;
        this.div.style.transform = `translate3d(${offsetX}px, ${offsetY}px, 0)`;
        this.div.style.left = '0px';
        this.div.style.top = '0px';
      }
    }

    onRemove() {
      if (this.root) {
        setTimeout(() => {
          this.root.unmount();
          this.root = null;
        }, 0);
      }

      if (this.div && this.div.parentNode) {
        this.div.parentNode.removeChild(this.div);
        this.div = null;
      }
    }

    setVisible(newVisible) {
      if (this.visible === newVisible) return;
      this.visible = newVisible;
      if (this.div) {
        this.div.style.zIndex = newVisible ? '1001' : '1';
      }
      this.renderOverlay();
    }

    renderOverlay() {
      if (!this.div) return;
      if (!this.root) this.root = createRoot(this.div);

      // Pass groupData and navigation handlers
      this.root.render(
        <OverlayContent
          groupData={this.groupData}
          visible={this.visible}
          setVisible={this.setVisible}
          onMarkerClick={this.onMarkerClick}
          language={this.language}
          selectedEventId={this.selectedEventId}
          disablePopups={this.disablePopups}
          onInfoBoxClick={this.onInfoBoxClick}
          onCreatorClick={this.onCreatorClick}
        />
      );
    }
  };
};
